import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Container,
  Button,
  VStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Select,
  Checkbox,
  CheckboxGroup,
  Spinner,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormHelperText,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Box,
  SimpleGrid,
  Wrap,
  WrapItem,
  Badge,
  Input,
  Switch,
  Icon,
  Tooltip
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { FiGlobe, FiMapPin, FiStar, FiInfo } from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import apiClient from '../../../../utils/apiClient'

const CreateLocale = () => {
  const navigate = useNavigate()
  const toast = useToast()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  // Form state
  const [locale, setLocale] = useState({
    code: '',
    name: '',
    native_name: '',
    site_ids: [] // Changed to simple array of site IDs
  })
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sites, setSites] = useState([])
  const [localeCodes, setLocaleCodes] = useState([])
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await apiClient(`/sites/${tenantId}`)
        setSites(response)
      } catch (error) {
        toast({
          title: 'Error fetching sites',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchLocaleCodes = async () => {
      try {
        const response = await fetch('https://cdn.simplelocalize.io/public/v1/locales')
        const data = await response.json()
        setLocaleCodes(data)
      } catch (error) {
        toast({
          title: 'Error fetching locale codes',
          description: 'Could not load locale codes',
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchData = async () => {
      await Promise.all([fetchSites(), fetchLocaleCodes()])
      setLoading(false)
    }

    fetchData()
  }, [tenantId])

  const handleLocaleChange = (e) => {
    const selectedCode = e.target.value
    const selectedLocale = localeCodes.find(lc => lc.locale === selectedCode)

    setLocale({
      ...locale,
      code: selectedCode,
      name: selectedLocale ? selectedLocale.language.name : '',
      native_name: selectedLocale ? selectedLocale.language.nativeName || selectedLocale.language.name : ''
    })

    // Clear errors when user makes a selection
    if (errors.code) {
      setErrors(prev => ({ ...prev, code: null }))
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setLocale(prev => ({ ...prev, [name]: value }))
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleSiteSelection = (siteId, isChecked) => {
    const siteIdInt = parseInt(siteId)
    
    if (isChecked) {
      // Add site ID to the array
      setLocale(prev => ({
        ...prev,
        site_ids: [...prev.site_ids, siteIdInt]
      }))
    } else {
      // Remove site ID from the array
      setLocale(prev => ({
        ...prev,
        site_ids: prev.site_ids.filter(id => id !== siteIdInt)
      }))
    }

    // Clear site selection errors
    if (errors.site_ids) {
      setErrors(prev => ({ ...prev, site_ids: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!locale.code.trim()) {
      newErrors.code = 'Locale code is required'
    }
    
    if (!locale.name.trim()) {
      newErrors.name = 'Locale name is required'
    }

    if (locale.site_ids.length === 0) {
      newErrors.site_ids = 'Please assign this locale to at least one site'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      // Create the locale with site assignments in one request
      const localePayload = {
        tenant_id: tenantId,
        code: locale.code.trim(),
        name: locale.name.trim(),
        native_name: locale.native_name.trim() || locale.name.trim(),
        site_ids: locale.site_ids
      }

      await apiClient('/locales', {
        method: 'POST',
        body: JSON.stringify(localePayload)
      })

      toast({
        title: 'Success',
        description: `Locale "${locale.name}" created and assigned to ${locale.site_ids.length} site(s)`,
        status: 'success',
        duration: 5000
      })

      navigate('/settings/locales')
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create locale',
        status: 'error',
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = locale.code.trim() && locale.name.trim() && locale.site_ids.length > 0 && Object.keys(errors).length === 0

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading locale options...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings/locales">Locales</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <HStack mb={2}>
            <Icon as={FiMapPin} color="purple.500" boxSize={6} />
            <Heading size="lg">Create New Locale</Heading>
          </HStack>
          <Text color="gray.500">
            Add a new language locale and assign it to your sites.
          </Text>
        </Box>

        {/* Form Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* Locale Information */}
              <Box>
                <Heading size="md" mb={4}>Locale Information</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired isInvalid={errors.code}>
                    <FormLabel>Locale Code</FormLabel>
                    <Select name="code" value={locale.code} onChange={handleLocaleChange}>
                      <option value="" disabled>Select a locale code</option>
                      {localeCodes.map((lc) => (
                        <option key={lc.locale} value={lc.locale}>
                          {lc.language.name} ({lc.locale})
                        </option>
                      ))}
                    </Select>
                    {errors.code ? (
                      <Text color="red.500" fontSize="sm" mt={1}>{errors.code}</Text>
                    ) : (
                      <FormHelperText>
                        Choose the ISO language code that represents this locale
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.name}>
                    <FormLabel>Locale Name</FormLabel>
                    <Input
                      name="name"
                      value={locale.name}
                      onChange={handleInputChange}
                      placeholder="Enter locale name"
                    />
                    {errors.name ? (
                      <Text color="red.500" fontSize="sm" mt={1}>{errors.name}</Text>
                    ) : (
                      <FormHelperText>
                        The display name for this locale (auto-filled based on code selection)
                      </FormHelperText>
                    )}
                  </FormControl>
                </SimpleGrid>

                <FormControl mt={4}>
                  <FormLabel>Native Name (Optional)</FormLabel>
                  <Input
                    name="native_name"
                    value={locale.native_name}
                    onChange={handleInputChange}
                    placeholder="Enter native name (e.g., Français for French)"
                  />
                  <FormHelperText>
                    The name of the language in its native script (auto-filled when available)
                  </FormHelperText>
                </FormControl>

                {/* Preview Badge */}
                {locale.code && locale.name && (
                  <Box mt={4}>
                    <Text fontSize="sm" color="gray.500" mb={2}>Preview:</Text>
                    <HStack>
                      <Badge colorScheme="blue" variant="subtle" p={2} borderRadius="md">
                        {locale.name} ({locale.code})
                      </Badge>
                      {locale.native_name && locale.native_name !== locale.name && (
                        <Badge colorScheme="purple" variant="subtle" p={2} borderRadius="md">
                          {locale.native_name}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Site Assignment Section */}
              <Box>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <HStack justify="space-between" align="center" mb={2}>
                      <HStack>
                        <Icon as={FiGlobe} color="blue.500" />
                        <Heading size="md">Site Assignments</Heading>
                      </HStack>
                      <Badge colorScheme="blue" variant="outline">
                        {locale.site_ids.length} selected
                      </Badge>
                    </HStack>
                    <Text color="gray.500" fontSize="sm">
                      Select which sites will use this locale.
                    </Text>
                  </Box>

                  {sites.length > 0 ? (
                    <FormControl isInvalid={errors.site_ids}>
                      <VStack spacing={3} align="stretch">
                        {sites.map(site => {
                          const isSelected = locale.site_ids.includes(site.id)
                          
                          return (
                            <Card
                              key={site.id}
                              variant="outline"
                              size="sm"
                              borderColor={isSelected ? "blue.200" : borderColor}
                              bg={isSelected ? "blue.50" : "transparent"}
                              transition="all 0.2s"
                              cursor="pointer"
                              onClick={() => handleSiteSelection(site.id, !isSelected)}
                            >
                              <CardBody p={4}>
                                <HStack justify="space-between" align="center">
                                  <Checkbox
                                    isChecked={isSelected}
                                    onChange={(e) => handleSiteSelection(site.id, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()} // Prevent double toggle
                                  >
                                    <VStack align="start" spacing={1}>
                                      <Text fontWeight="medium" fontSize="sm">
                                        {site.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {site.domain || 'No domain set'}
                                      </Text>
                                    </VStack>
                                  </Checkbox>

                                  {isSelected && (
                                    <Badge colorScheme="blue" variant="solid" size="sm">
                                      <Icon as={FiGlobe} mr={1} />
                                      Selected
                                    </Badge>
                                  )}
                                </HStack>
                              </CardBody>
                            </Card>
                          )
                        })}
                      </VStack>
                      {errors.site_ids && (
                        <Text color="red.500" fontSize="sm" mt={2}>{errors.site_ids}</Text>
                      )}
                    </FormControl>
                  ) : (
                    <Alert status="warning" borderRadius="md">
                      <AlertIcon />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">No Sites Available</Text>
                        <Text fontSize="sm">
                          You need to create at least one site before creating a locale.
                        </Text>
                      </VStack>
                    </Alert>
                  )}
                </VStack>
              </Box>

              {/* Assignment Summary */}
              {locale.site_ids.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="medium" fontSize="sm">Assignment Summary</Text>
                        <HStack wrap="wrap" spacing={2}>
                          <Text fontSize="sm">This locale will be assigned to:</Text>
                          {sites
                            .filter(site => locale.site_ids.includes(site.id))
                            .map(site => (
                              <Badge key={site.id} colorScheme="blue" variant="subtle">
                                {site.name}
                              </Badge>
                            ))
                          }
                        </HStack>
                      </VStack>
                    </Alert>
                  </Box>
                </>
              )}

              <Divider />

              {/* Action Buttons */}
              <HStack spacing={4} justify="flex-end">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/settings/locales')}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmit}
                  isDisabled={!isFormValid}
                  isLoading={isSubmitting}
                  loadingText="Creating..."
                >
                  Create Locale
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default CreateLocale
